/**
 * TimerPage — Batch #14.235
 *
 * Tuile Cabinet > Timer : sessions de travail à taux horaire.
 * Chronomètre en direct + saisie manuelle + archive avec totaux.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Timer, Play, Square, X, Plus, Trash2,
  CalendarDays, Wallet, ListChecks,
} from "lucide-react";

const fmtDuration = (min) => {
  if (!min && min !== 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`;
};
const fmtElapsed = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};
const clientLabel = (c) =>
  `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || c.email || c.id;
const today = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Toronto" }).format(new Date());

export default function TimerPage() {
  const [clients, setClients] = useState([]);
  const [jobs, setJobs] = useState([]);
  // chrono
  const [running, setRunning] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const tickRef = useRef(null);
  const [clientId, setClientId] = useState("");
  const [jobId, setJobId] = useState("");
  const [rate, setRate] = useState(() => localStorage.getItem("timer_last_rate") || "");
  // jobs manager
  const [showJobs, setShowJobs] = useState(false);
  const [newJob, setNewJob] = useState("");
  // saisie manuelle
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ date: today(), start: "", end: "" });
  // archive
  const [filterClient, setFilterClient] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [data, setData] = useState({ rows: [], totals: { count: 0, minutes: 0, amount: 0 }, by_client: [] });

  const loadJobs = useCallback(async () => {
    try {
      const r = await api.get("/admin/timer/jobs");
      setJobs(r.data || []);
      if ((r.data || []).length === 0) setShowJobs(true);
    } catch { /* silent */ }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const params = {};
      if (filterClient) params.client_id = filterClient;
      if (filterMonth) params.month = filterMonth;
      const r = await api.get("/admin/timer/sessions", { params });
      setData(r.data || { rows: [], totals: { count: 0, minutes: 0, amount: 0 }, by_client: [] });
    } catch { toast.error("Impossible de charger les sessions"); }
  }, [filterClient, filterMonth]);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/clients"); setClients((r.data || []).sort((a, b) => clientLabel(a).localeCompare(clientLabel(b)))); } catch { /* silent */ }
      loadJobs();
      try {
        const r = await api.get("/admin/timer/running");
        if (r.data?.running) { setRunning(r.data.running); setElapsed(r.data.elapsed_seconds || 0); }
      } catch { /* silent */ }
    })();
  }, [loadJobs]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // tick du chrono
  useEffect(() => {
    if (running) {
      tickRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      return () => clearInterval(tickRef.current);
    }
  }, [running]);

  const startTimer = async () => {
    try {
      const r = await api.post("/admin/timer/start", {
        client_id: clientId, job_id: jobId, hourly_rate: parseFloat(rate),
      });
      localStorage.setItem("timer_last_rate", rate);
      setRunning(r.data); setElapsed(0);
      toast.success("Chronomètre démarré");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur au démarrage"); }
  };

  const stopTimer = async () => {
    try {
      const r = await api.post(`/admin/timer/stop/${running.id}`);
      setRunning(null); setElapsed(0);
      toast.success(`Session enregistrée : ${fmtDuration(r.data.duration_minutes)} — ${r.data.amount.toFixed(2)} $`);
      loadSessions();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur à l'arrêt"); }
  };

  const cancelTimer = async () => {
    if (!window.confirm("Annuler la session en cours ? Rien ne sera enregistré.")) return;
    try {
      await api.delete(`/admin/timer/cancel/${running.id}`);
      setRunning(null); setElapsed(0);
      toast.message("Session annulée");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const addJob = async () => {
    const name = newJob.trim();
    if (!name) return;
    try {
      const r = await api.post("/admin/timer/jobs", { name });
      setNewJob(""); setJobs((j) => [...j, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setJobId(r.data.id);
      toast.success(`Job « ${name} » ajoutée`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Supprimer la job « ${job.name} » ? Les sessions passées gardent leur nom.`)) return;
    try {
      await api.delete(`/admin/timer/jobs/${job.id}`);
      setJobs((j) => j.filter((x) => x.id !== job.id));
      if (jobId === job.id) setJobId("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const saveManual = async () => {
    try {
      const r = await api.post("/admin/timer/sessions", {
        client_id: clientId, job_id: jobId, date: manual.date,
        start_time: manual.start, end_time: manual.end, hourly_rate: parseFloat(rate),
      });
      localStorage.setItem("timer_last_rate", rate);
      setManual({ date: today(), start: "", end: "" });
      setShowManual(false);
      toast.success(`Session ajoutée : ${fmtDuration(r.data.duration_minutes)} — ${r.data.amount.toFixed(2)} $`);
      loadSessions();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur à l'enregistrement"); }
  };

  const deleteSession = async (row) => {
    if (!window.confirm(`Supprimer la session du ${row.date} (${row.client_name}) ?`)) return;
    try { await api.delete(`/admin/timer/sessions/${row.id}`); loadSessions(); toast.message("Session supprimée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const formReady = clientId && jobId && rate !== "" && parseFloat(rate) >= 0;
  const manualReady = formReady && manual.date && manual.start && manual.end;
  const liveAmount = running ? ((elapsed / 3600) * (running.hourly_rate || 0)) : 0;

  const inputCls = "text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white w-full";

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-5xl mx-auto" data-testid="timer-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Timer className="w-6 h-6 text-teal-600" />
          Timer
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">
          Sessions de travail à taux horaire — chronomètre en direct ou saisie manuelle.
        </p>
      </div>

      {/* Chronomètre */}
      <div className={`border-2 rounded-2xl p-4 sm:p-6 ${running ? "bg-teal-50 border-teal-300" : "bg-white border-slate-200"}`}>
        {!running ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Client</label>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls} data-testid="timer-client-select">
                  <option value="">— Choisir un client —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Job</label>
                <div className="flex gap-1.5">
                  <select value={jobId} onChange={(e) => setJobId(e.target.value)} className={inputCls} data-testid="timer-job-select">
                    <option value="">{jobs.length === 0 ? "Aucune job — créez-en une →" : "— Choisir une job —"}</option>
                    {jobs.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
                  </select>
                  <button
                    onClick={() => setShowJobs((v) => !v)}
                    className={`px-2.5 rounded-md border text-sm font-semibold flex-shrink-0 inline-flex items-center gap-1 transition ${
                      showJobs
                        ? "bg-teal-600 text-white border-teal-600"
                        : "bg-teal-50 text-teal-700 border-teal-300 hover:bg-teal-100"
                    }`}
                    title="Créer / gérer les jobs"
                    data-testid="timer-manage-jobs-btn"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Job</span>
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Taux horaire ($/h)</label>
                <input type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)}
                  placeholder="ex : 60" className={inputCls} data-testid="timer-rate-input" />
              </div>
            </div>

            {showJobs && (
              <div className="bg-slate-50 border rounded-xl p-3 space-y-2" data-testid="timer-jobs-manager">
                <p className="text-xs font-semibold text-slate-600">
                  {jobs.length === 0 ? "Créez votre première job (ex : Tenue de livres, Impôts, Paie) :" : "Vos jobs :"}
                </p>
                <div className="flex gap-2">
                  <input value={newJob} onChange={(e) => setNewJob(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addJob()}
                    placeholder="Nouvelle job (ex : Tenue de livres)" className={inputCls} data-testid="timer-new-job-input" />
                  <button onClick={addJob} className="px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm inline-flex items-center gap-1 flex-shrink-0" data-testid="timer-add-job-btn">
                    <Plus className="w-4 h-4" /> Ajouter
                  </button>
                </div>
                {jobs.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {jobs.map((j) => (
                      <span key={j.id} className="text-xs px-2 py-1 rounded-full bg-white border border-slate-200 inline-flex items-center gap-1.5">
                        {j.name}
                        <button onClick={() => deleteJob(j)} className="text-slate-400 hover:text-red-500" data-testid={`timer-job-delete-${j.id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <button onClick={startTimer} disabled={!formReady}
                className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"
                data-testid="timer-start-btn">
                <Play className="w-4 h-4" /> Démarrer le chronomètre
              </button>
              <button onClick={() => setShowManual((v) => !v)}
                className="px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm inline-flex items-center gap-2"
                data-testid="timer-manual-toggle">
                <CalendarDays className="w-4 h-4" /> Saisie manuelle
              </button>
            </div>

            {showManual && (
              <div className="bg-slate-50 border rounded-xl p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end" data-testid="timer-manual-form">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Date</label>
                  <input type="date" value={manual.date} onChange={(e) => setManual({ ...manual, date: e.target.value })} className={inputCls} data-testid="timer-manual-date" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Début</label>
                  <input type="time" value={manual.start} onChange={(e) => setManual({ ...manual, start: e.target.value })} className={inputCls} data-testid="timer-manual-start" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Fin</label>
                  <input type="time" value={manual.end} onChange={(e) => setManual({ ...manual, end: e.target.value })} className={inputCls} data-testid="timer-manual-end" />
                </div>
                <button onClick={saveManual} disabled={!manualReady}
                  className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-sm disabled:opacity-40"
                  data-testid="timer-manual-save">
                  Enregistrer
                </button>
                <p className="col-span-2 sm:col-span-4 text-[11px] text-slate-400 -mt-1">
                  Utilise le client, la job et le taux sélectionnés ci-dessus.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="text-center sm:text-left flex-1">
              <div className="text-4xl sm:text-5xl font-bold tabular-nums text-teal-800" data-testid="timer-elapsed">
                {fmtElapsed(elapsed)}
              </div>
              <p className="text-sm text-teal-700 mt-1">
                {clientLabel(clients.find((c) => c.id === running.client_id) || { id: running.client_id })}
                {" · "}{running.job_name}
                {" · "}{(running.hourly_rate || 0).toFixed(2)} $/h
              </p>
              <p className="text-xs text-teal-600 mt-0.5">
                Démarré à {running.start_time} — en cours : ≈ {liveAmount.toFixed(2)} $
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={stopTimer}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center gap-2"
                data-testid="timer-stop-btn">
                <Square className="w-4 h-4" /> Arrêter et enregistrer
              </button>
              <button onClick={cancelTimer}
                className="px-3 py-2 rounded-lg border border-slate-300 hover:bg-white text-sm text-slate-600"
                data-testid="timer-cancel-btn">
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Totaux */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white border-2 border-slate-200 rounded-xl p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-slate-500 mb-1 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Sessions</div>
          <div className="text-2xl sm:text-3xl font-bold text-slate-800" data-testid="timer-total-sessions">{data.totals.count}</div>
        </div>
        <div className="bg-white border-2 border-teal-200 rounded-xl p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-teal-700 mb-1 flex items-center gap-1"><Timer className="w-3.5 h-3.5" /> Heures</div>
          <div className="text-2xl sm:text-3xl font-bold text-teal-700" data-testid="timer-total-hours">{fmtDuration(data.totals.minutes)}</div>
        </div>
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-3 sm:p-4">
          <div className="text-[11px] sm:text-xs text-emerald-700 mb-1 flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Montant</div>
          <div className="text-2xl sm:text-3xl font-bold text-emerald-700" data-testid="timer-total-amount">{data.totals.amount.toFixed(2)} $</div>
        </div>
      </div>

      {/* Filtres archive */}
      <div className="bg-white border rounded-xl p-3 sm:p-4 flex items-center gap-3 flex-wrap">
        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1.5" data-testid="timer-filter-client">
          <option value="">Tous les clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
        </select>
        <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="text-xs border border-slate-200 rounded-md px-2 py-1" data-testid="timer-filter-month" />
        {(filterClient || filterMonth) && (
          <button onClick={() => { setFilterClient(""); setFilterMonth(""); }} className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="timer-clear-filters">
            Effacer les filtres
          </button>
        )}
      </div>

      {/* Par client */}
      {!filterClient && data.by_client.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="timer-by-client">
          {data.by_client.map((bc, i) => (
            <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800">
              {bc.client_name} : <b>{fmtDuration(bc.minutes)}</b> · {bc.amount.toFixed(2)} $
            </span>
          ))}
        </div>
      )}

      {/* Archive */}
      <div className="bg-white border rounded-xl divide-y" data-testid="timer-sessions-list">
        {data.rows.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">Aucune session enregistrée.</div>
        )}
        {data.rows.map((row) => (
          <div key={row.id} className="p-3 sm:p-4 flex items-center gap-3 hover:bg-slate-50/50" data-testid={`timer-session-row-${row.id}`}>
            <div className="w-9 h-9 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
              <Timer className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {row.client_name} <span className="text-slate-400 font-normal">·</span> {row.job_name}
              </p>
              <p className="text-xs text-slate-500">
                {row.date} — {row.start_time} → {row.end_time}
                {row.manual && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">manuel</span>}
              </p>
            </div>
            <span className="text-sm font-medium text-slate-700 tabular-nums flex-shrink-0">{fmtDuration(row.duration_minutes)}</span>
            <span className="text-xs text-slate-400 tabular-nums flex-shrink-0 hidden sm:inline">{(row.hourly_rate || 0).toFixed(0)} $/h</span>
            <span className="text-sm font-bold text-emerald-700 tabular-nums flex-shrink-0">{(row.amount || 0).toFixed(2)} $</span>
            <button onClick={() => deleteSession(row)} className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`timer-session-delete-${row.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
